import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { playerAttendanceService } from '../../services/playerAttendanceService';
import { X } from 'lucide-react';
import { MobileSelect } from '../ui/MobileSelect';

interface EventAttendancePanelProps {
  eventType: 'practice' | 'game';
  eventId: string;
  eventDate: string;
  eventTime?: string;
  teamId: string;
  title: string;
  onClose?: () => void;
}

type AttendanceException = 'late' | 'no_show' | 'excused' | null;

interface PlayerWithAttendance {
  id: string;
  first_name: string;
  last_name: string;
  number: number | null;
  exception: AttendanceException;
  attendanceId?: string;
  notes?: string;
}

/**
 * Marks per-player attendance exceptions for a single practice or game.
 * Generalized from the practice-only version so the same UI/logic can be
 * reused wherever a coach needs to log attendance for an event.
 */
export function EventAttendancePanel({
  eventType,
  eventId,
  eventDate,
  eventTime,
  teamId,
  title,
  onClose,
}: EventAttendancePanelProps) {
  const [players, setPlayers] = useState<PlayerWithAttendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  // Fetch players and existing attendance
  useEffect(() => {
    fetchData();
  }, [eventType, eventId, teamId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch players that have player cards (matching roster behavior)
      // This ensures attendance only tracks players that are in the roster
      const { data: playerCards, error: cardsError } = await supabase
        .from('player_cards')
        .select(`
          player_id,
          player:players!inner(id, first_name, last_name, number)
        `)
        .eq('team_id', teamId);

      if (cardsError) throw cardsError;

      // Extract unique players from player cards
      const uniquePlayers = (playerCards || [])
        .map(card => card.player)
        .filter((player, index, self) =>
          player && index === self.findIndex(p => p?.id === player.id)
        )
        .sort((a, b) => {
          const lastNameA = (a?.last_name || '').toLowerCase();
          const lastNameB = (b?.last_name || '').toLowerCase();
          return lastNameA.localeCompare(lastNameB);
        }) as Array<{ id: string; first_name: string; last_name: string; number: number | null }>;

      // Fetch existing attendance exceptions for this event
      const existingAttendance = await playerAttendanceService.getAttendanceForEvent(
        eventType,
        eventId
      );

      // Map players with their attendance status
      const playersWithAttendance: PlayerWithAttendance[] = uniquePlayers.map(player => {
        const attendance = existingAttendance.find(a => a.player_id === player.id);
        return {
          id: player.id,
          first_name: player.first_name,
          last_name: player.last_name,
          number: player.number,
          exception: attendance ? (attendance.status as AttendanceException) : null,
          attendanceId: attendance?.id,
          notes: attendance?.notes || undefined,
        };
      });

      setPlayers(playersWithAttendance);
    } catch (error) {
      console.error(`[EventAttendancePanel] Error fetching ${eventType} attendance data:`, error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleExceptionChange = async (
    playerId: string,
    exception: AttendanceException
  ) => {
    setSaving(prev => ({ ...prev, [playerId]: true }));

    try {
      if (exception === null) {
        // Remove exception (mark as present)
        const player = players.find(p => p.id === playerId);
        if (player?.attendanceId) {
          await playerAttendanceService.deleteAttendance(player.attendanceId);
        }
      } else {
        // Add or update exception
        const player = players.find(p => p.id === playerId);

        if (player?.attendanceId) {
          // Update existing record
          await playerAttendanceService.updateAttendance(player.attendanceId, {
            status: exception,
          });
        } else {
          // Create new record
          await playerAttendanceService.logAttendance(
            playerId,
            teamId,
            eventType,
            eventId,
            exception,
            eventDate,
            eventTime
          );
        }
      }

      // Refresh data
      await fetchData();
      toast.success('Attendance updated');
    } catch (error) {
      console.error(`[EventAttendancePanel] Error updating ${eventType} attendance:`, error);
      toast.error('Failed to update attendance');
    } finally {
      setSaving(prev => ({ ...prev, [playerId]: false }));
    }
  };

  const getExceptionColor = (exception: AttendanceException) => {
    switch (exception) {
      case 'late':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'no_show':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'excused':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-green-50 border-green-200 text-green-800';
    }
  };

  const exceptionCounts = {
    present: players.filter(p => p.exception === null).length,
    late: players.filter(p => p.exception === 'late').length,
    noShow: players.filter(p => p.exception === 'no_show').length,
    excused: players.filter(p => p.exception === 'excused').length,
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">Loading attendance...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            All players are assumed present. Mark exceptions below.
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-2xl font-bold text-green-800">{exceptionCounts.present}</div>
          <div className="text-xs text-green-600">Present</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="text-2xl font-bold text-yellow-800">{exceptionCounts.late}</div>
          <div className="text-xs text-yellow-600">Late</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-2xl font-bold text-red-800">{exceptionCounts.noShow}</div>
          <div className="text-xs text-red-600">No Show</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-2xl font-bold text-blue-800">{exceptionCounts.excused}</div>
          <div className="text-xs text-blue-600">Excused</div>
        </div>
      </div>

      {/* Player List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {players.map(player => {
          const isSaving = saving[player.id];
          return (
            <div
              key={player.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${getExceptionColor(
                player.exception
              )} ${isSaving ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3 flex-1">
                {player.number && (
                  <div className="w-8 h-8 bg-white/50 rounded-full flex items-center justify-center text-sm font-medium">
                    {player.number}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {player.first_name} {player.last_name}
                  </div>
                  {player.notes && (
                    <div className="text-xs opacity-75 mt-0.5">{player.notes}</div>
                  )}
                </div>
              </div>

              <MobileSelect
                value={player.exception || 'present'}
                onChange={(value) => {
                  handleExceptionChange(
                    player.id,
                    value === 'present' ? null : (value as AttendanceException)
                  );
                }}
                disabled={isSaving}
                options={[
                  { value: 'present', label: 'Present' },
                  { value: 'late', label: 'Late (after warmups)' },
                  { value: 'no_show', label: 'No Show' },
                  { value: 'excused', label: 'Excused Absence' },
                ]}
                className="ml-4 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Attendance status"
                sheetTitle="Attendance Status"
              />
            </div>
          );
        })}
      </div>

      {/* Info Note */}
      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs text-gray-600">
          <strong>Note:</strong> Late and No Show are derogatory marks that count against attendance.
          Excused absences are tracked but do not count against attendance.
        </p>
      </div>
    </div>
  );
}

export default EventAttendancePanel;
